import { ReactNode, RefObject, useEffect, useRef } from 'react';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { hidePopover } from '@shared/util/popover';
import { getController } from '@shared/util/timeout';
import classNames from './Bezel.module.css';

// Public API for `bezel()` calls
export interface BezelProps {
  message?: string | ReactNode;
  className?: string;
}

interface BezelComponentProps extends BezelProps {
  // Set by `BezelProvider` only, to unmount the bezel after it has animated out.
  onClose: VoidFunction;
}

export function Bezel({ message, className, onClose }: BezelComponentProps) {
  const bezelRef = useRef<HTMLDivElement>(null);

  // Show the popover. Needs to be called only once, so no dependencies
  useShowPopover({ bezelRef });

  // Notify the presenter when the bezel has animated out
  useBezelAnimationEnd({ bezelRef, onClose });

  return (
    <View ref={bezelRef} popover="manual" className={[classNames.bezel, className]}>
      <Text className={classNames.message}>{message}</Text>
    </View>
  );
}

interface PopoverEffect {
  bezelRef: RefObject<HTMLDivElement>;
  onClose: VoidFunction;
}

function useShowPopover({ bezelRef }: Pick<PopoverEffect, 'bezelRef'>) {
  useEffect(() => {
    const element = bezelRef.current;
    if (!element) {
      return;
    }

    element.showPopover();
    return () => {
      hidePopover(element);
    };
  }, [bezelRef]);
}

function useBezelAnimationEnd({ bezelRef, onClose }: PopoverEffect) {
  useEffect(() => {
    const element = bezelRef.current;
    if (!element) {
      return;
    }

    const onAnimationEnd = (event: AnimationEvent) => {
      if (event.animationName !== classNames.bounceOut) {
        return;
      }
      if (hidePopover(element)) {
        onClose();
      }
    };
    const { signal, abort } = getController();
    element.addEventListener('animationend', onAnimationEnd, { signal });
    return abort;
  }, [bezelRef, onClose]);
}
